import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';

interface VideoPlayerProps {
  src: string;
  className?: string;
}

function formatDuration(seconds: number): string {
  if (!isFinite(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const PLAY_EVENT = 'sossiety:video:play';

export const VideoPlayer = ({ src, className = '' }: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState<string>('');

  // Read duration from metadata
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onMeta = () => setDuration(formatDuration(video.duration));
    video.addEventListener('loadedmetadata', onMeta);
    return () => video.removeEventListener('loadedmetadata', onMeta);
  }, []);

  // Pause when another video starts playing
  useEffect(() => {
    const onOtherPlay = (e: Event) => {
      if ((e as CustomEvent).detail !== videoRef.current) {
        videoRef.current?.pause();
        setPlaying(false);
      }
    };
    window.addEventListener(PLAY_EVENT, onOtherPlay);
    return () => window.removeEventListener(PLAY_EVENT, onOtherPlay);
  }, []);

  // Pause when < 20% visible (IntersectionObserver)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < 0.2 && !video.paused) {
          video.pause();
          setPlaying(false);
        }
      },
      { threshold: [0, 0.2] }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    // notify others to pause
    window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: video }));
    video.play();
    setPlaying(true);
  };

  return (
    <div className={`relative bg-[#1F1010] rounded-xl overflow-hidden aspect-video ${className}`}>
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        preload="metadata"
        muted
        playsInline
        controls={playing}
        onEnded={() => setPlaying(false)}
      />

      {/* Play overlay */}
      {!playing && (
        <button
          onClick={handlePlay}
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-black/20"
        >
          <span className="w-13 h-13 w-[52px] h-[52px] rounded-full bg-sossiety-red flex items-center justify-center shadow-lg">
            <Play className="w-6 h-6 text-white fill-white ml-0.5" />
          </span>
        </button>
      )}

      {/* Duration badge */}
      {duration && !playing && (
        <span className="absolute bottom-2 right-2 text-[11px] text-white font-medium bg-black/60 px-1.5 py-0.5 rounded">
          {duration}
        </span>
      )}
    </div>
  );
};
